import {
  isBookingProviderUnavailableError,
  isBookingProviderUncertainError,
  isExpiredSearchSessionError,
  isInternalError,
  isPaymentProviderUnavailableError,
  isProviderUnavailableError,
} from '@massilia/queries'
import { AxiosError } from 'axios'

import { parseSearchSessionExpiryReason } from './search-session-error'

/**
 * Ce qui a échoué, du point de vue du client — la seule question à laquelle un
 * écran d'erreur doit répondre avant de proposer une sortie.
 *
 * Le site en comptait autant de versions que de surfaces : la sheet, les trois
 * frontières de route, les cartes en ligne du tunnel. Chacune rangeait ce
 * qu'elle ne savait pas nommer dans son propre bac générique, avec la copie du
 * voisin — d'où « Vérifiez votre connexion » sur une panne de nos serveurs.
 */
export type ClientErrorType =
  /** Affidium n'a pas confirmé : un dossier existe peut-être, ne surtout pas relancer. */
  | 'booking-uncertain'
  /** L'appel n'a jamais atteint le site : coupure, délai dépassé. */
  | 'network'
  /** L'offre existe toujours, mais son tarif a bougé de plus d'un euro. */
  | 'price-changed'
  /** Un prestataire ne répond pas — vols ou paiement. */
  | 'provider-down'
  /** Une panne de notre côté : 5xx, ou le repli `INTERNAL_ERROR`. */
  | 'server-error'
  /** L'offre n'existe plus dans notre cache : la recherche est trop ancienne. */
  | 'session-expired'
  /** Un refus que nous n'avons pas nommé — le message du serveur parle pour lui. */
  | 'unknown'

/**
 * Range une erreur dans l'un des sept cas ci-dessus.
 *
 * L'ordre des tests est la seule chose qui compte ici : un
 * `BOOKING_PROVIDER_UNCERTAIN` est un 503 comme les autres pannes prestataire,
 * mais il interdit de relancer — il doit donc être reconnu avant elles. Et
 * `isInternalError` accepte tout 5xx, y compris ces 503 : il vient en dernier
 * des pannes, comme filet.
 */
export function resolveClientErrorType(error: Error): ClientErrorType {
  if (isBookingProviderUncertainError(error)) return 'booking-uncertain'

  if (
    isProviderUnavailableError(error) ||
    isBookingProviderUnavailableError(error) ||
    isPaymentProviderUnavailableError(error)
  ) {
    return 'provider-down'
  }

  if (isExpiredSearchSessionError(error)) {
    return parseSearchSessionExpiryReason(error) === 'PRICE_CHANGED'
      ? 'price-changed'
      : 'session-expired'
  }

  if (isInternalError(error)) return 'server-error'

  // Une `AxiosError` sans réponse n'a jamais atteint le serveur : c'est le
  // transport qui a lâché, et le conseil à donner n'est pas le même.
  if (error instanceof AxiosError && !error.response) return 'network'

  return 'unknown'
}
